import { Avatar, Col, Icon, Picture, Tappable, Text, useTheme } from "@rally/ui-kit";
import { formatTime } from "../../localization/format.ts";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { useFace } from "../../me/face.ts";
import { usePresent } from "../../push/here.ts";
import type { Person } from "../composer/saying.ts";
import { type ListedFile, useOpened } from "../filed.ts";
import { useStack } from "../panel/panels.tsx";
import { fileCard } from "./file-resource.tsx";
import { drawsInline, glyphFor, sizeSaid, typeSaid } from "./filing.ts";

const PICTURE = 260;

export function FiledBody({
  file,
  conversation,
  onDrawn,
}: {
  file: ListedFile;
  conversation: string;
  onDrawn?: () => void;
}) {
  const theme = useTheme();
  const picture = drawsInline(file);
  const url = useOpened(file.id);
  const stack = useStack();

  return picture ? (
    <Tappable
      onPress={() => stack.open(fileCard(file, conversation))}
      accessibilityRole="button"
      accessibilityLabel={file.name}
      testID={`filed-${file.id}-picture`}
      style={{
        borderRadius: theme.radius.md,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        alignSelf: "flex-start",
        maxWidth: "100%",
      }}
    >
      {url ? (
        <Picture
          source={{ uri: url }}
          accessibilityLabel={file.name}
          resizeMode="contain"
          onLoad={onDrawn}
          style={{ width: 360, maxWidth: "100%", height: PICTURE }}
        />
      ) : (
        <Col style={{ width: 240, height: PICTURE, backgroundColor: theme.color["bg.surfaceAlt"] }} />
      )}
    </Tappable>
  ) : (
    <Tappable
      onPress={() => stack.open(fileCard(file, conversation))}
      accessibilityRole="button"
      accessibilityLabel={file.name}
      testID={`filed-${file.id}-row`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[3],
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        borderRadius: theme.radius.md,
        paddingVertical: theme.space[2],
        paddingHorizontal: theme.space[3],
        alignSelf: "flex-start",
        maxWidth: "100%",
      }}
    >
      <Icon name={glyphFor(file)} size={18} color="text.secondary" />
      <Col style={{ minWidth: 0, flexShrink: 1 }}>
        <Text typeName="body" color="text.primary" numberOfLines={1}>
          {file.name}
        </Text>
        <Text typeName="caption" color="text.tertiary">
          {`${typeSaid(file)} · ${sizeSaid(file.bytes)}`}
        </Text>
      </Col>
    </Tappable>
  );
}

export function Filed({
  file,
  who,
  onDrawn,
  conversation,
}: {
  file: ListedFile;
  conversation: string;
  who: Person | undefined;
  onDrawn?: () => void;
}) {
  const theme = useTheme();
  const face = useFace(who?.avatar);
  const name = calledBy(who) ?? say("chat.someone");
  const when = formatTime(file.at);

  return (
    <Col style={{ flexDirection: "row", gap: theme.space[3] }} testID={`filed-${file.id}`}>
      <Avatar name={name} src={face} size={32} present={usePresent(who?.account)} />
      <Col style={{ flex: 1, minWidth: 0, gap: theme.space[1] }}>
        <Col style={{ flexDirection: "row", alignItems: "baseline", gap: theme.space[2] }}>
          <Text typeName="label" color="text.primary">
            {name}
          </Text>
          <Text typeName="caption" color="text.tertiary">
            {when}
          </Text>
        </Col>
        <FiledBody file={file} conversation={conversation} onDrawn={onDrawn} />
      </Col>
    </Col>
  );
}
